import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { HttpStatusCode } from 'axios';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import {
  FileReportSendResponseViewModel,
  FileReportUploadResponse,
  PayrollDetailsFileViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import {
  mockFileReportSendResponse,
  mockFileReportUploadResponse,
} from '@fora-monorepo/employer-my-pages-common/utils';
import { trackException, trackTrace } from '@fora-monorepo/webapp-analytics';

import { envConfig } from '../../config';
import { createThunkError } from '../utils';
import { compressFileWithGzip } from './utils';
import { mockPayrollReportCSV } from '../report/mocks';
import { typedT } from '../../i18n/i18n';

export const sendFileReport = createAsyncThunk('sendFileReport', async (fileReportUploadUID: string, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockFileReportSendResponse.success;
  }
  return axios
    .post(`${envConfig.bffBaseUri}/file-report/${fileReportUploadUID}/send`)
    .then(res => {
      if (res.status === 200) {
        return res.data as FileReportSendResponseViewModel;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not confirm file report', err));
    });
});

export const uploadFileReport = createAsyncThunk(
  'uploadFileReport',
  async ({ file, useCompression }: { file: File; useCompression: boolean }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockFileReportUploadResponse.success;
    } else {
      const form = new FormData();

      // If CompressionStream is supported by current browser, compress file before upload
      if (useCompression && 'CompressionStream' in window) {
        const compressedBlob = await compressFileWithGzip(file);
        form.append('fileReport', compressedBlob, file.name);
        form.append('useCompression', 'true');
      } else {
        form.append('fileReport', file);
      }

      return axios
        .post(`${envConfig.bffBaseUri}/file-report/upload`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          if (res.status === 200) {
            return res.data as FileReportUploadResponse;
          }
        })
        .catch(err => {
          trackException(err);
          // Not using createThunkError since the payload is used in the slice
          if (err.response?.status === HttpStatusCode.PayloadTooLarge) {
            return thunkAPI.rejectWithValue([typedT('errorMessages.fileUpload.fileSizeTooLarge')]);
          }
          if (err.response?.data?.message) {
            const errorMessage: string = err.response.data.message;
            return thunkAPI.rejectWithValue([errorMessage]);
          }
          return thunkAPI.rejectWithValue([typedT('errorMessages.fileUpload.generalFailure')]);
        });
    }
  }
);

export const getPayrollDetailsCSVFromFileUid = createAsyncThunk(
  'getPayrollDetailsCSVFromFileUid',
  async (
    {
      fileReportUploadUid,
      reportIndex,
      reportedAtTimestamp,
    }: { fileReportUploadUid: string; reportIndex: number; reportedAtTimestamp: string },
    thunkAPI
  ) => {
    if (envConfig.mockDataFrontend) {
      return mockPayrollReportCSV;
    } else {
      return axios
        .get(`${envConfig.bffBaseUri}/file-report/${fileReportUploadUid}/${reportIndex}/download/payroll-details`, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            reportedAtTimestamp,
          },
        })
        .then(res => {
          if (res.status === 200) {
            return res.data as PayrollDetailsFileViewModel;
          } else if (res.status === 204) {
            trackTrace(
              `csv for report with fileReportUploadUid: ${fileReportUploadUid} returned 204 no content`,
              SeverityLevel.Warning
            );
            return null;
          }
        })
        .catch(err => {
          trackException(err);
          return thunkAPI.rejectWithValue(
            createThunkError(`Could not get csv for fileReportUploadUid: ${fileReportUploadUid}`)
          );
        });
    }
  }
);
